// One row in the bulk-add review list. The title is read-only; the only editable
// field is the inline tag line (BulkTagLine — a single-line, non-grouped variant
// of word-detail's TagEditor). The × cross-out is a reversible exclude — a
// crossed-out word stays visible, struck through, and offers an Undo. A
// conflicting row shows a "Conflit" badge + a resolve button. The flat child
// order is flex-laid as a single line on desktop and wraps into a stacked card
// on mobile (see .bulk-row in styles.css).
import { BulkTagLine } from '@/components/words/bulk-add/bulk-tag-line';
import type { BulkWord } from '@/lib/bulk-add/types';
import type { NoyauKey, WordTag } from '@/schemas/word';

interface BulkReviewRowProps {
  word: BulkWord;
  /** The batch noyau colour key for the leading dot, or null (indéterminé). */
  noyauKey: NoyauKey | null;
  /** Whether this word is part of an unresolved conflict. */
  inConflict: boolean;
  onTagsChange: (tags: WordTag[]) => void;
  onToggleExclude: () => void;
  onResolveConflict: () => void;
}

/** A single reviewable word row. */
export function BulkReviewRow({
  word,
  noyauKey,
  inConflict,
  onTagsChange,
  onToggleExclude,
  onResolveConflict,
}: BulkReviewRowProps) {
  return (
    <li
      id={`bulk-row-${word.id}`}
      className={`bulk-row${word.excluded ? ' is-excluded' : ''}${inConflict ? ' is-conflict' : ''}`}
    >
      <span
        className={`bulk-row__dot ${noyauKey ? `words-noyau-dot--${noyauKey}` : 'words-noyau-dot--none'}`}
        aria-hidden="true"
      />

      <span className="bulk-row__title">
        {word.title}
        {word.op.kind === 'mergeInto' && (
          <span className="bulk-row__merged">
            → fusionné avec «&nbsp;{word.op.targetTitle}&nbsp;»
          </span>
        )}
        {inConflict && <span className="bulk-badge bulk-badge--conflict">Conflit</span>}
        {word.failed && <span className="bulk-badge bulk-badge--error">Erreur</span>}
      </span>

      <div className="bulk-row__tags">
        <BulkTagLine tags={word.tags} readOnly={word.excluded} onChange={onTagsChange} />
      </div>

      {inConflict && (
        <button type="button" className="bulk-row__resolve" onClick={onResolveConflict}>
          Résoudre le conflit
        </button>
      )}

      <div className="bulk-row__cross-wrap">
        {word.excluded ? (
          <button type="button" className="bulk-row__undo" onClick={onToggleExclude}>
            <span className="material-symbols-rounded" aria-hidden="true">
              undo
            </span>
            Annuler
          </button>
        ) : (
          <button
            type="button"
            className="bulk-row__cross"
            aria-label={`Exclure « ${word.title} »`}
            onClick={onToggleExclude}
          >
            <span className="material-symbols-rounded" aria-hidden="true">
              close
            </span>
          </button>
        )}
      </div>
    </li>
  );
}
